<template>
    <form class="user-data-form-container" @submit.prevent="submitForm">
        <h1 class="title">{{ $t("personalDataForm.title") }}</h1>
        <!-- First name -->
        <div v-if="getBurnField(1)" class="form-field">
            <input
                id="first-name-input"
                type="text"
                v-model="firstName"
                :placeholder="firstNameLabel"
                @blur="onFieldBlur(fieldNames.firstName)"
                @keydown="clearFieldError('firstName')"
            />
            <div v-if="errors?.firstName?.length" class="error-message">
                {{ $t("personalDataForm.errors." + errors.firstName) }}
            </div>
        </div>

        <!-- Last name -->
        <div v-if="getBurnField(2)" class="form-field">
            <input
                id="last-name-input"
                type="text"
                v-model="lastName"
                :placeholder="lastNameLabel"
                @blur="onFieldBlur(fieldNames.lastName)"
                @keydown="clearFieldError('lastName')"
            />
            <div v-if="errors?.lastName?.length" class="error-message">
                {{ $t("personalDataForm.errors." + errors.lastName) }}
            </div>
        </div>

        <!-- Email -->
        <div v-if="getBurnField(3)" class="form-field">
            <input
                id="email-input"
                type="email"
                v-model="email"
                :placeholder="emailLabel"
                @blur="onFieldBlur(fieldNames.email)"
                @input="onEmailInput"
                @keydown="clearFieldError('email')"
            />
            <div v-if="errors?.email?.length" class="error-message">
                {{ $t("personalDataForm.errors." + errors.email) }}
            </div>
        </div>

        <!-- Phone -->
        <div v-if="getBurnField(4)" class="form-field">
            <input
                id="phone-input"
                type="tel"
                v-model="phone"
                :placeholder="phoneLabel"
                @blur="onFieldBlur(fieldNames.phone)"
                @keydown="clearFieldError('phone')"
            />
            <div v-if="errors?.phone?.length" class="error-message">
                {{ $t("personalDataForm.errors." + errors.phone) }}
            </div>
        </div>

        <!-- Video Agreement -->
        <div v-if="qrcodeObject?.project?.videoAgreementTypeId !== 0" class="form-field">
            <CheckBox :text="videoAgreementText" :value="videoAgreement" @input="toggleAgreement" />
            <div v-if="errors?.videoAgreement?.length" class="error-message">
                {{ $t("personalDataForm.errors." + errors.videoAgreement) }}
            </div>
        </div>

        <transition name="fade" mode="out-in">
            <div class="submit-container" v-if="!isPending">
                <input type="submit" :disabled="formInvalid" :value="$t('general.validate')" />
            </div>
            <Loader class="loader" v-else
        /></transition>
    </form>
</template>

<script lang="ts">
import { defineComponent } from "vue";

import Loader from "@/components/Loader.vue";
import CheckBox from "@/components/Checkbox.vue";
import { State } from "@/store";
import { mapState } from "vuex";

const phoneRegex = /^\+?[0-9\s]+$/;

enum FieldNames {
    firstName = 1,
    lastName = 2,
    email = 3,
    phone = 4,
    videoAgreement = "videoAgreement"
}

export default defineComponent({
    name: "PersonalDataForm",
    components: {
        Loader,
        CheckBox
    },
    data() {
        return {
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
            videoAgreement: false,
            consentChecked: false,
            isPending: false,
            errors: {} as { [key: string]: string },
            fieldNames: FieldNames
        };
    },
    computed: {
        formInvalid() {
             const numericFieldNames = Object.values(FieldNames).filter(
                (v): v is number => typeof v === 'number'
            );

            return this.qrcodeObject.project.shortlinkBurnFields.some(field =>
                numericFieldNames.some(key =>
                field.fieldId === key && this.checkField(key as FieldNames).length > 0
                )
            );
        },
        ...mapState({
            qrcodeObject: (state: State) => state.qrcode.qrcodeObject
        }),
        firstNameLabel() {
            let label = this.$t("personalDataForm.firstNameLabel");

            if (this.getBurnField(1).isRequired) {
                label += "*";
            }

            return label;
        },
        lastNameLabel() {
            let label = this.$t("personalDataForm.lastNameLabel");

            if (this.getBurnField(2).isRequired) {
                label += "*";
            }
            return label;
        },
        emailLabel() {
            let label = this.$t("personalDataForm.emailLabel");

            if (this.getBurnField(3).isRequired) {
                label += "*";
            }

            return label;
        },
        phoneLabel() {
            let label = this.$t("personalDataForm.phoneLabel");

            if (this.getBurnField(4).isRequired) {
                label += "*";
            }

            return label;
        },
        videoAgreementText() {
            let text = this.qrcodeObject.project.agreementMessage;

            if (this.qrcodeObject?.project?.videoAgreementTypeId === 2) {
                text += "*";
            }

            return text;
        }
    },
    methods: {
        getBurnField(id: number) {
            return this.qrcodeObject?.project?.shortlinkBurnFields.find(
                field => field.fieldId === id
            );
        },
        async submitForm() {
            const videoAgreementField = this.checkField(FieldNames.videoAgreement);

            if (videoAgreementField) {
                this.errors[FieldNames.videoAgreement] = videoAgreementField;
                return;
            }

            this.$store.dispatch("record/setUserData", {
                firstName: this.firstName,
                lastName: this.lastName,
                email: this.email,
                phone: this.phone,
                videoAgreement: this.videoAgreement
            });
            this.$router.push("Upload");
        },
        clearFieldError(fieldName: string) {
            delete this.errors[fieldName];
        },
        toggleAgreement(value: boolean) {
            this.videoAgreement = value;
            this.clearFieldError(FieldNames.videoAgreement);
        },
        checkField(fieldName: FieldNames) {
            let result = "";
            let field;
            switch (fieldName) {
                case FieldNames.firstName:
                    field = this.getBurnField(1);
                    if (field && field.isRequired && !this.firstName.length) {
                        result = "fieldRequired";
                    }
                    break;

                case FieldNames.lastName:
                    field = this.getBurnField(2);
                    if (field && field.isRequired && !this.lastName.length) {
                        result = "fieldRequired";
                    }
                    break;

                case FieldNames.email:
                    field = this.getBurnField(3);
                    if (field && field.isRequired && !this.email.length) {
                        result = "fieldRequired";
                    }
                    break;

                case FieldNames.videoAgreement:
                    if (
                        this.qrcodeObject?.project?.videoAgreementTypeId === 2 &&
                        !this.videoAgreement
                    ) {
                        result = "videoAgreement";
                    }
                    break;

                case FieldNames.phone:
                    field = this.getBurnField(4);
                    if (field && field.isRequired && !this.phone.length) {
                        result = "fieldRequired";
                    } else if (
                        !this.getBurnField(4).isRequired &&
                        this.phone.length &&
                        !phoneRegex.test(this.phone)
                    ) {
                        result = "fieldValueIncorrect";
                    }
                    break;
            }
            return result;
        },
        onFieldBlur(fieldName: FieldNames) {
            const errors = this.checkField(fieldName);
            this.errors[fieldName] = errors;
        },
        onEmailInput(ev: Event) {
            const input = ev.target as HTMLInputElement;
            input.value = input.value.toLowerCase();
        }
    }
});
</script>

<style scoped>
h1 {
    margin: 0;
    color: var(--screenTextColor);
}

.user-data-form-container {
    width: 95%;
    align-self: center;
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 25px;
    align-items: center;
    justify-items: center;
    margin-top: 25px;
}

.form-field {
    display: flex;
    flex-direction: column;
}

.form-field > *:nth-child(1) {
    margin-bottom: 5px;
}

input[type="email"],
input[type="text"],
input[type="tel"] {
    padding: 10px 20px;
    border-radius: calc(1em + 10px);
    line-height: 1em;
    background: var(--white);
    color: var(--black);
    border: 0;
    box-shadow: 2px 3px 5px 0px rgb(150 150 150 / 50%);
}

.submit-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

input[type="submit"] {
    appearance: none;
    margin-bottom: 5px;
    padding: 10px 20px;
    border-radius: calc(1em + 10px);
    line-height: 1em;
    border: 0;
    background: var(--secondary);
    color: var(--buttonTextColor);
    transition: filter 0.2s ease-in;
}

input[type="submit"]:disabled {
    opacity: 0.5;
}

.loader {
    margin: auto;
}

.error-message {
    color: var(--red);
    font-size: 0.95em;
}
</style>
